
import { h, Fragment, Component, createRef } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { onIntersection } from '@util/intersection'
import { withHydration } from '@util/hydrate'

import { Audio, Play, Pause } from '@components/icons'

import './style.less'

const secondsToTimer = time => {
	const isNumber = (typeof time === 'number' && time === time)
	if (!isNumber) return "--:--"
	const pad = (num, size) => ('000' + num).slice(size * -1)
	const hours = Math.floor(time / 60 / 60)
	const minutes = Math.floor(time / 60) % 60
	const seconds = Math.floor(time - minutes * 60)

	const sep = <span class="timer-sep"/>

	return <>
		{ !!hours && <><span class="tabular">{hours}</span>:</> }
		<span class="tabular">{ pad(minutes, hours ? 2 : 1) }</span>:<span class="tabular">{ pad(seconds, 2) }</span>
	</>
}

const videoPlaying = el => {
	return (
		el.currentTime > 0 
		&& !el.paused 
		&& !el.ended 
		&& el.readyState > 2
	)
}


class VideoAutoplayScroll extends Component {

	constructor(props) {
		super(props)
		this.state = { 
			inView: false
		}
		this.videoRef = createRef()
		this.lastInteractionRef = createRef(null)
	}

	componentDidMount() {
		// set width and height based on intrinsic video size
		this.videoRef.current.addEventListener('canplay', event => {
			const video = event.currentTarget
			video.width = video.videoWidth
			video.height = video.videoHeight
			video.style.aspectRatio = `${video.width} / ${video.height}`
		})

		// set the inView state when scrolled into/out of view
		this.cancelIntersection = onIntersection(this.videoRef.current, intersect => {
			this.setState({ 
				inView: (
					intersect &&
					intersect?.intersectionRatio > 0 ||
					intersect?.isIntersecting || 
					intersect?.isVisible
				) 
			})
		}, '0px 0px')
	}

	componentWillUnmount = () => this.cancelIntersection()

	shouldComponentUpdate(props, state) {
		
		const video = this.videoRef.current
		const isPlaying = videoPlaying(video)

		// reflect muted property
		video.muted = !!props.muted

		// pause when scrolling out of view
		if (isPlaying && !state.inView) {
			video.pause()
			return false
		}

		// play when scrolling into view:
		// if no interaction, or the last interaction was 'play'
		if (
			props.autoplay 
			&& !isPlaying 
			&& state.inView
			&& (
				this.lastInteractionRef.current === null
				|| this.lastInteractionRef.current === 'play'
			)
		) {
			video.play()
			return false
		}

		// set user playing state to video state
		if (
			props.playing !== null 
			&& props.playing !== isPlaying 
			&& state.inView
		) {
			if (!isPlaying && props.playing === true) {
				this.lastInteractionRef.current = 'play'
				video.play()
				return false
				
			} else if (isPlaying && props.playing === false) {
				this.lastInteractionRef.current = 'pause'
				video.pause()
				return false
			}
		}

		// never rerender the video
		return false
	}

	render() {
		const props = this.props
		props.playing = undefined
		return <video ref={this.videoRef} {...props} />
	}
}



const PauseButton = ({ ...props }) => {
	return <button class="" {...props}>
		<Audio mute={true}></Audio>
	</button>
}

const WithControls = ({ ...props }) => {
	
	const [userPlay, setUserPlay] = useState(null)
	const [isPlaying, setIsPlaying] = useState(null)
	const [isLoading, setIsLoading] = useState(null)
	const [isMuted, setIsMuted] = useState(props.autoplay)

	const onPlay = (videoState) => setIsPlaying(true)
	const onPause = (videoState) => setIsPlaying(false)

	const [time, setTime] = useState(0)
	const [duration, setDuration] = useState(null)

	const onTimeUpdate = (event) => {
		setIsLoading(event.currentTarget.readyState <= 2)
		setTime(event.currentTarget.currentTime)
		setDuration(event.currentTarget.duration)
	}

	const playAndUnmute = () => {
		setIsMuted(false)
		setUserPlay(true)
	}

	const showOverlay = isMuted || !isPlaying && !isLoading

	const classes = [
		'video-inline',
		'text--large',
		isPlaying ? 'video-inline--playing' : 'video-inline--paused'
	].filter(Boolean).join(' ')

	return (
		<div class={classes}>
			<div class="video-inline__main">
				<VideoAutoplayScroll 
					playsinline
					crossorigin="anonymous"
					width="100%" height="auto"
					{...props}
					muted={isMuted}
					playing={userPlay}
					onPlay={onPlay}
					onPause={onPause}
					onCanPlay={onTimeUpdate}
					onTimeUpdate={onTimeUpdate}
					onClick={e => {
						const video = e.currentTarget
						const isPlaying = videoPlaying(video)
						setUserPlay(!isPlaying)
					}}
				/>
				{ isLoading && 
					<div class="video-inline__overlay" style="pointer-events:none">
						Loading…
					</div>
				}
				{ showOverlay && 
					<div 
						class="video-inline__overlay video-inline__overlay--when-paused video-inline__overlay--hover" 
						onClick={playAndUnmute}
					>
						{ isPlaying 
							? <>
								<Audio mute={isMuted}></Audio>
								<span>{ isMuted && 'Unmute' || 'Mute' }</span>
							</>
							: <>
								<Play />
								<span>{ isPlaying && 'Pause' || 'Play' }</span>
							</>
						}
					</div>
				}
			</div>

			<div class="video-inline__controls">
				<div class="video-inline__controls__left">
					<div className="video-inline__control">
						<button class="video-inline__button" onClick={() => setUserPlay(!isPlaying)}>
							{ isPlaying && <Pause/> || <Play /> }
							<span>{ isPlaying && 'Pause' || 'Play' }</span>
						</button>
					</div>
				</div>
				<div class="video-inline__controls__right">
					<div className="video-inline__control hide show@small">
						<button class="video-inline__button video-inline__button--icon-right" onClick={() => setIsMuted(!isMuted)}>
							<span class="video-inline__button__text--on-hover">{ isMuted && 'Unmute' || 'Mute' }</span>
							<Audio mute={isMuted}></Audio>
						</button>
					</div>
					
					<div className="video-inline__control video-inline__control__time">
						{ secondsToTimer(time) }<span class="fade">/{ secondsToTimer(duration) }</span>
					</div>
				</div>
			</div>
		</div>
	)
}

const WithoutControls = ({ ...props }) => {
	return <VideoAutoplayScroll 
		playsinline
		loop
		autoplay
		crossorigin="anonymous"
		width="100%" height="auto"
		{...props}
	/>
}

export const VideoInlineControls = withHydration(WithControls, 'VideoInlineControls')

export default withHydration(WithoutControls, 'VideoInline')




